import React from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingUp as TrendingIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { Navigate, useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/customer/CustomerLayout';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useCustomerPortal } from '../../contexts/CustomerPortalContext';

const features = [
  {
    icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Fast & Reliable',
    description: 'Lightning-fast AI-powered solutions that scale with your needs'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Enterprise Security',
    description: 'Bank-level encryption and compliance with industry standards'
  },
  {
    icon: <TrendingIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Analytics & Insights',
    description: 'Real-time analytics and actionable insights for your business'
  }
];

const benefits = [
  'No credit card required for trial',
  'Cancel anytime with one click',
  '24/7 customer support',
  'Money-back guarantee'
];

const CustomerLandingPageV2: React.FC = () => {
  const navigate = useNavigate();
  // Esta landing es PÚBLICA (un visitante anónimo debe verla entera), pero es
  // puro discurso comercial de marketplace: prueba gratuita, «Ver precios»,
  // devolución del dinero. A un cliente ya autenticado cuya cuenta gestiona un
  // PARTNER no le corresponde nada de eso, así que se le lleva a su cuenta. Se
  // espera a saberlo (spinner) para no enseñarle precios ni medio segundo.
  const { isAuthenticated } = useCustomerAuth();
  const { managedByReseller, loading } = useCustomerPortal();

  if (isAuthenticated && loading) {
    return (
      <CustomerLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </CustomerLayout>
    );
  }

  if (isAuthenticated && managedByReseller) {
    return <Navigate to="/customer/account" replace />;
  }

  return (
    <CustomerLayout>
      {/* Hero Section - Clean & Direct */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 10 }
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" maxWidth="800px" mx="auto">
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 2,
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              AI Solutions for Modern Business
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                color: '#666',
                fontWeight: 400,
                fontSize: { xs: '1.1rem', md: '1.3rem' }
              }}
            >
              Transform your operations with intelligent automation and insights.
              Start your free trial today.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/customer/register')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)'
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/customer/plans')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                View Pricing
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Features Section - Clean Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1a1a1a' }}>
            Why Choose WAI
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            Everything you need to automate and scale your business operations
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box mb={2}>{feature.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Start Risk-Free
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CheckIcon color="success" />
                  <Typography variant="body1">{benefit}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/customer/register')}
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2
              }}
            >
              Get Started Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Simple Footer CTA */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Ready to Transform Your Business?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Join thousands of companies using WAI to automate and grow
        </Typography>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/customer/login')}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2
          }}
        >
          Sign In
        </Button>
      </Container>
    </CustomerLayout>
  );
};

export default CustomerLandingPageV2;

